import * as React from 'react'
import { useCreateOppActions } from '../../reducer'
import { SideDrawer, SideDrawerForm } from '@sales-center/components'
import { SmallLocationCard } from '@user/components/Location'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import { ENTITY_TYPES } from '@sales-center/common'
import { useToggleBoolean } from '@user/utils/useToggleBoolean'
import { getStyles } from './styling'
import { AddLocationCheckbox } from './AddLocationCheckbox'
import { useToggleBooleanMap } from '@user/utils/useToggleBooleanMap'
import { selectAddOpportunitySlice } from '@user/store/opportunity/addOpportunity/addOpportunitySlice'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material/styles'
import { useFilterServiceLocations } from './useFilterServiceLocations'

type SelectServiceLocationDrawerProps = ReturnType<typeof useCreateOppActions>

export const ServiceLocationsDrawer: React.FC<
  SelectServiceLocationDrawerProps
> = (props) => {
  const { state, onClickApply, onClickCancel, onCheckServiceLocation } = props

  const drawerOpen =
    !!state.primaryServiceLocation &&
    !state.loading &&
    state.appliedSelections === undefined

  const [billToOpen, toggleBillToOpen] = useToggleBoolean()
  const [servLocOpen, toggleServLocOpen] = useToggleBooleanMap()

  const { billTo, serviceLocations } = useSelector(selectAddOpportunitySlice)

  const { searchText, onChangeSearchText, filteredLocations } =
    useFilterServiceLocations(serviceLocations)

  const totalSelected = state.selectedServiceLocations.size
  const selectedInView = React.useMemo(
    () =>
      filteredLocations.filter((loc) =>
        state.selectedServiceLocations.has(loc.id.toString()),
      ).length,
    [filteredLocations, state.selectedServiceLocations],
  )

  const locationLabel = `Service Location${
    filteredLocations.length === 1 ? '' : 's'
  }`

  const selectionDetails =
    totalSelected === 0
      ? `No ${locationLabel} Selected`
      : `${selectedInView} / ${filteredLocations.length} ${locationLabel} Selected${
          totalSelected > selectedInView ? ` (${totalSelected} total)` : ''
        }`

  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <SideDrawer
      open={drawerOpen}
      onClose={onClickCancel}
      title="Create Opportunity"
    >
      <SideDrawerForm
        onSave={onClickApply}
        primaryBtnLabel="Apply"
        onCancel={onClickCancel}
        padding="24px"
        disableSave={state.selectedServiceLocations.size === 0}
        saveBtnDataTestId="service-locations-apply-button"
        cancelBtnDataTestId="service-locations-cancel-button"
      >
        <Box sx={styles.label}>
          Choose one or more service locations related to this Bill-to
        </Box>
        {state.primaryServiceLocation && (
          <SmallLocationCard
            className={styles.billToCard}
            entityId={-1}
            entityType={ENTITY_TYPES.Opportunity}
            location={billTo}
            hidePopover
            isEditable={false}
            open={billToOpen}
            toggleOpen={toggleBillToOpen}
          />
        )}
        <Box sx={styles.searchBox}>
          <TextField
            value={searchText}
            onChange={onChangeSearchText}
            fullWidth
            size="small"
            sx={{ minHeight: 44 }}
          />
        </Box>
        <Box sx={styles.label}>{selectionDetails}</Box>
        {filteredLocations.map((loc) => (
          <SmallLocationCard
            className={styles.serviceLocationCard}
            entityId={-1}
            entityType={ENTITY_TYPES.Opportunity}
            hidePopover
            isEditable={false}
            open={!!servLocOpen.get(loc.id.toString())}
            key={loc.id}
            location={loc}
            toggleOpen={() => toggleServLocOpen(loc.id.toString())}
            header={
              <AddLocationCheckbox
                checked={state.selectedServiceLocations.has(loc.id.toString())}
                onChange={() => onCheckServiceLocation(loc.id.toString())}
              />
            }
            style={{
              background: state.selectedServiceLocations.has(loc.id.toString())
                ? '#F4F8FA'
                : theme.palette.common.white,
            }}
          />
        ))}
      </SideDrawerForm>
    </SideDrawer>
  )
}
ServiceLocationsDrawer.displayName = 'ServiceLocationsDrawer'
